/**
 * CollapsibleSection - Expandable Section Component
 *
 * Reusable accordion-style section for organizing multiple indicators.
 * Supports future scalability (15+ indicators per roadmap).
 * Matches institutional-grade styling.
 */

import React, { useState, memo } from 'react';
import { Box, Typography, Chip, Collapse, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * CollapsibleSection Component
 */
const CollapsibleSection = memo(({
    title,
    icon = null,
    badge = null,
    badgeColor = '#fbbf24',
    phase = null,
    defaultExpanded = false,
    disabled = false,
    children,
    onToggle = null
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const handleToggle = () => {
        if (disabled) return;
        const newState = !expanded;
        setExpanded(newState);
        if (onToggle) onToggle(newState);
    };

    return (
        <Box
            sx={{
                borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
                '&:last-child': { borderBottom: 'none' }
            }}
        >
            {/* Section Header - Always Visible */}
            <Box
                onClick={handleToggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    backgroundColor: expanded ? 'rgba(220, 38, 38, 0.08)' : 'transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: disabled ? 'transparent' : 'rgba(55, 65, 81, 0.25)'
                    }
                }}
            >
                {/* Left: Icon + Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: expanded ? '#ef4444' : '#9ca3af',
                            transition: 'transform 0.2s ease',
                            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}
                    >
                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    </Box>
                    {icon && (
                        <Box sx={{ color: expanded ? '#ef4444' : '#9ca3af', display: 'flex' }}>
                            {icon}
                        </Box>
                    )}
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: expanded ? '#ef4444' : '#b4bcd0',
                            letterSpacing: '0.5px',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase'
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                {/* Right: Badge or Phase Indicator */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {badge && !phase && (
                        <Chip
                            label={badge}
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                                color: badgeColor,
                                fontSize: '9px',
                                height: '16px',
                                fontWeight: 700,
                                '& .MuiChip-label': { px: 0.75 }
                            }}
                        />
                    )}
                    {phase && (
                        <Tooltip title={`Coming in Phase ${phase}`} arrow>
                            <Chip
                                label={`P${phase}`}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(156, 163, 175, 0.2)',
                                    color: '#9ca3af',
                                    fontSize: '8px',
                                    height: '14px',
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 0.5 }
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Section Content - Collapsible */}
            <Collapse in={expanded && !disabled} timeout={200}>
                <Box
                    sx={{
                        padding: '4px 8px 8px 8px',
                        backgroundColor: '#0f0f0f'
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
});

CollapsibleSection.displayName = 'CollapsibleSection';

export default CollapsibleSection;
